import { request, RequestInitWithRetry } from '../../../lib/request';
import { ISortResult, VIEW } from '../common';
import { IFilterResult } from '../../../components/filters/filterSelectionControl';
import { get } from 'lodash';

export interface IDevice {
  id: string,
  imei?: string,
  serialNumber?: string,
  model: string,
  manufacturer: string,
  updated?: string,
  assetTag?: string,
  averageDischargeStatus: string,
  timeZone?: { name: string } | null,
  smartBatteries: {
    id: string,
    partNumber: string,
    serialNumber: string,
    capacityFactor: number,
    cycleCount: number,
    batteryStatus: string,
    averageDischarge: number,
    averageDischargeStatus: string,
    cycleCountStatus: string,
    healthStatus: string
  }[]
}

export interface IDeviceParams {
  statusType: string,
  status?: string[],
  limit: number,
  offset: number,
  groups?: string[],
  manufacturers?: string[],
  models?: string[],
  homeLocations?: string[],
  groups_negate?: boolean,
  manufacturers_negate?: boolean,
  models_negate?: boolean,
  homeLocations_negate?: boolean,
  sort?: ISortResult,
  search?: string,
  pinnedId?: string
}

export interface IDeviceWithDischargeStatus {
  id: string,
  manufacturer: string,
  model: string,
  imei?: string,
  serialNumber?: string,
  assetTag?: string,
  updated?: string,
  group?: string[],
  homeLocation?: {
    id: string,
    name: string
  },
  averageDischarge?: number,
  averageDischargeStatus?: string,
  averageDischargeTime: number
}

export interface IDevicesWithCount {
  red: number,
  yellow: number,
  green: number,
  all: number
}

export interface IDevicesAvgDischargeTimeIntervalStats {
  hour0To1: number,
  hour1To2: number,
  hour2To3: number,
  hour3To4: number,
  hour4To5: number,
  hour5To6: number,
  hour6To7: number,
  hour7To8: number,
  hour8To9: number,
  hour9To10: number,
  hour10AndAbove: number
}

export interface IDevicesAvgDischargeRateIntervalStats {
  ZeroToTen: number,
  TenToTwenty: number,
  TwentyToThirty: number,
  ThirtyToForty: number,
  FortyToFifty: number,
  FiftyToSixty: number,
  SixtyToSeventy: number,
  SeventyToEighty: number,
  EightyToNinety: number,
  NinetyToHundred: number,
  HundredAbove: number
}

export const deviceNegateKeys = ['groups_negate', 'manufacturers_negate', 'models_negate', 'homeLocations_negate'] as const;
export type DeviceNegateKey = typeof deviceNegateKeys[number];

// Forward only the negate flags that are explicitly enabled, mirroring the Core devices list behaviour
export const getNegateParams = (source: Partial<Record<DeviceNegateKey, unknown>> = {}): Partial<Record<DeviceNegateKey, boolean>> => {
  return deviceNegateKeys.reduce((acc, key) => {
    if (source[key]) {
      acc[key] = true;
    }
    return acc;
  }, {} as Partial<Record<DeviceNegateKey, boolean>>);
};

export class BatteryDevicesService {
  public getList = (options: RequestInitWithRetry) => async (params: IDeviceParams): Promise<IDeviceWithDischargeStatus[]> => {
    const input = {
      path: `/api/batteryEssentials/getDevicesWithDischargeStatus`,
      qs: { statusType: params.statusType === VIEW.all ? undefined : params.statusType, limit: params.limit, offset: params.offset, pinnedId: get(params, 'pinnedId') },
      body: {
        groups: get(params, 'groups'),
        manufacturers: get(params, 'manufacturers'),
        models: get(params, 'models'),
        homeLocations: get(params, 'homeLocations'),
        sort: get(params, 'sort'),
        search: get(params, 'search'),
        status: get(params, 'status'),
        ...getNegateParams(params)
      },
      method: 'post'
    };
    const result = await request(input, options);
    return result.devices.nodes;
  };

  public exportList = (options: RequestInitWithRetry) => async (params: IDeviceParams): Promise<any> => {
    const input = {
      path: `/api/batteryEssentials/exportDevicesWithDischargeStatus`,
      qs: { statusType: params.statusType === VIEW.all ? undefined : params.statusType, limit: params.limit, offset: params.offset },
      body: {
        groups: get(params, 'groups'),
        manufacturers: get(params, 'manufacturers'),
        models: get(params, 'models'),
        homeLocations: get(params, 'homeLocations'),
        status: get(params, 'status'),
        search: get(params, 'search'),
        ...getNegateParams(params)
      },
      method: 'post'
    };

    return await request(input, options);
  };

  public getDeviceEstateData = (options: RequestInitWithRetry) => async (search?: string, filters?: IFilterResult): Promise<IDevicesWithCount> => {
    const input = {
      path: `/api/batteryEssentials/getDevicesWithDischargeCount`,
      body: {
        groups: get(filters, 'groups'),
        manufacturers: get(filters, 'manufacturers'),
        models: get(filters, 'models'),
        homeLocations: get(filters, 'homeLocations'),
        search: search || undefined
      },
      method: 'post'
    };

    return await request(input, options);
  };

  public getDeviceEstateDataWithFilters = (options: RequestInitWithRetry) => async (search?: string, filters?: IFilterResult): Promise<number> => {
    const input = {
      path: `/api/batteryEssentials/getDevicesWithDischargeCountWithFilters`,
      body: {
        status: get(filters, 'status'),
        groups: get(filters, 'groups'),
        manufacturers: get(filters, 'manufacturers'),
        models: get(filters, 'models'),
        homeLocations: get(filters, 'homeLocations'),
        search: search || undefined,
        ...getNegateParams(filters)
      },
      method: 'post'
    };

    return await request(input, options);
  };

  public getDeviceDetails = (options: RequestInitWithRetry) => async (deviceId: string): Promise<IDevice> => {
    const input = {
      path: `/api/batteryEssentials/getDeviceWithDischargeStatus`,
      qs: { deviceId },
      method: 'get'
    };

    return await request(input, options);
  };

  public getLast30DaysDevicesAvgDischargeTimeIntervalStats = (options: RequestInitWithRetry) => async (): Promise<IDevicesAvgDischargeTimeIntervalStats | null> => {
    const input = {
      path: `/api/batteryEssentials/last30DaysDevicesDischargeTimeIntervalStats`,
      method: 'get'
    };

    return await request(input, options);
  };

  public getLast30DaysDevicesAvgDischargeRateIntervalStats = (options: RequestInitWithRetry) => async (): Promise<IDevicesAvgDischargeRateIntervalStats | null> => {
    const input = {
      path: `/api/batteryEssentials/last30DaysDevicesDischargeRateIntervalStats`,
      method: 'get'
    };

    return await request(input, options);
  };
}
